/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — MEDIA VIEWER

   One viewer, shared by the PRODUCT GALLERY, the product page's worn-by strip,
   the homepage feed carousel and the editorial figures. Every one of them hands
   it the same three things: a URL template carrying a {width} placeholder, the
   source's real pixel width, and an optional video. Nothing else is duplicated
   anywhere — the product gallery in particular has no second copy of its media.

   RESOLUTION IS THE POINT, TWICE OVER.

   A preview tile is loaded at tile size, so opening it must not scale that file
   up: the viewer asks for what the viewport can show, at open. And zooming is
   not "the same file, bigger" — as the scale climbs the viewer asks for a
   LARGER rendition, so 3x on a 3000px source is real detail rather than four
   soft pixels pretending to be one. It never asks for more than the merchant
   uploaded, and MAX_SCALE is derived from the source's own width, so a small
   original simply cannot be magnified into mush.

   Built on the theme's own overlay manager (HV.overlays), so the scrim, the
   body scroll lock, the focus trap and Escape behave exactly the way the cart
   drawer and the search overlay already do. One place decides what is open —
   adding a second would be how the lock gets stuck.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  /* A ceiling on anything we will ever fetch. Beyond this the file costs more
     than the detail is worth, even when the merchant uploaded more. */
  const MAX_SRC = 3000;
  /* Below this there is not enough headroom for zoom to be worth offering, so
     the controls hide rather than promising something the source cannot give. */
  const ZOOM_FLOOR = 1.15;

  const dpr = () => Math.min(window.devicePixelRatio || 1, 2);

  /* Ask for exactly the pixels this moment needs — never past the source, never
     past MAX_SRC, rounded up to 200 so repeat asks land on one cached file. */
  function sizedUrl(template, natural, targetPx) {
    if (!template) return '';
    const nat = Number(natural) || MAX_SRC;
    const cap = Math.min(targetPx, MAX_SRC, nat);
    const step = Math.min(Math.ceil(cap / 200) * 200, nat);
    return template.replace('{width}', String(Math.max(200, Math.round(step))));
  }

  class HvViewer extends HTMLElement {
    connectedCallback() {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.dialog = $('[data-viewer-dialog]', this);
      this.stage = $('[data-viewer-stage]', this);
      this.caption = $('[data-viewer-caption]', this);
      this.counter = $('[data-viewer-counter]', this);
      this.zoomBox = $('[data-viewer-zoom]', this);
      this.level = $('[data-viewer-level]', this);
      this.zoomIn = $('[data-viewer-zoom-in]', this);
      this.zoomOut = $('[data-viewer-zoom-out]', this);
      if (!this.dialog || !this.stage) return;

      this.pointers = new Map();
      this.resetZoom();

      /* Delegated, so cards added by a section re-render are covered without
         rebinding anything — and so there is exactly one click listener on the
         document no matter how many galleries are on the page. */
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-viewer-open]');
        if (!trigger) return;
        /* A real control inside the card still wins. Links were already exempt;
           BUTTONS were not, and a video tile carries two of them. VIDEO is
           exempt too: a product video plays where it sits, and its own controls
           must not be a way to accidentally throw the lightbox open. */
        /* ONLY a video with its OWN controls is exempt (a product-page video).
           Feed and UGC tiles are bare autoplaying <video> elements that fill
           the whole tile, so exempting every <video> meant a click on a clip
           tile could never open the viewer at all. */
        if (e.target.closest('a[href]:not([data-viewer-open]), button:not([data-viewer-open]), video[controls]')) return;
        e.preventDefault();
        this.openFrom(trigger);
      }, { signal });

      /* ENTER AND SPACE HAVE TO OPEN IT TOO. Every trigger carries role="button"
         and tabindex="0", which promises a keyboard user that it operates like a
         button. It did not: a div with role=button does NOT synthesise a click
         the way a real <button> does, so the listener above could never fire
         from the keyboard and the whole viewer was mouse-only. */
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        const trigger = e.target.closest && e.target.closest('[data-viewer-open]');
        if (!trigger) return;
        if (e.target !== trigger && e.target.closest('a[href],button,video[controls]')) return;
        e.preventDefault();
        this.openFrom(trigger);
      }, { signal });

      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'Escape') {
          /* The overlay manager also handles Escape, but it only removes the
             class — it cannot know this element has a playing video to stop.
             close() is idempotent, so both running is harmless. */
          this.close();
          return;
        }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === '+' || e.key === '=') { e.preventDefault(); this.nudgeZoom(1); }
        if (e.key === '-' || e.key === '_') { e.preventDefault(); this.nudgeZoom(-1); }
        if (e.key === '0') { e.preventDefault(); this.setZoom(1); }
      }, { signal });

      $('[data-viewer-close]', this)?.addEventListener('click', () => this.close(), { signal });
      $('[data-viewer-next]', this)?.addEventListener('click', () => this.step(1), { signal });
      $('[data-viewer-prev]', this)?.addEventListener('click', () => this.step(-1), { signal });
      this.zoomIn?.addEventListener('click', () => this.nudgeZoom(1), { signal });
      this.zoomOut?.addEventListener('click', () => this.nudgeZoom(-1), { signal });

      /* Backdrop closes — the dialog itself, and the dark stage around the
         photograph. A click on the photograph zooms instead, and a click that
         is really the tail of a pan must not close anything at all. */
      this.dialog.addEventListener('click', (e) => {
        if (this.swallowClick) { this.swallowClick = false; return; }
        if (e.target === this.dialog || e.target === this.stage) this.close();
      }, { signal });
      document.addEventListener('click', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.target.id === 'hv-scrim') this.close();
      }, { signal });

      /* ── zoom + pan ──────────────────────────────────────────────────── */
      this.stage.addEventListener('wheel', (e) => this.onWheel(e), { signal, passive: false });
      this.stage.addEventListener('pointerdown', (e) => this.onPointerDown(e), { signal });
      this.stage.addEventListener('pointermove', (e) => this.onPointerMove(e), { signal });
      this.stage.addEventListener('pointerup', (e) => this.onPointerUp(e), { signal });
      this.stage.addEventListener('pointercancel', (e) => this.onPointerUp(e), { signal });
      this.stage.addEventListener('lostpointercapture', () => this.endDrag(), { signal });
      addEventListener('blur', () => this.endDrag(), { signal });
      /* the fit changes with the viewport, so the zoom ceiling has to as well */
      addEventListener('resize', () => { if (this.classList.contains('is-open')) this.measureBase(); }, { signal });
    }

    disconnectedCallback() {
      this.clearStage();
      if (this.controller) { this.controller.abort(); this.controller = null; }
    }

    /* The collection a card belongs to is its own gallery, so next/prev walks
       the row you opened from rather than every picture on the page. For the
       product page that scope is the gallery stage, which means prev/next is
       exactly the product's own media, in the thumbnails' order. */
    openFrom(trigger) {
      const scope = trigger.closest('[data-viewer-scope]') || document;
      this.scope = scope;
      this.items = $$('[data-viewer-open]', scope);
      this.index = this.items.indexOf(trigger);
      if (this.index < 0) this.index = 0;
      this.returnTo = trigger;
      this.render();

      this.classList.add('is-open');
      this.removeAttribute('hidden');
      if (window.HV && HV.overlays) {
        /* the theme's overlay manager owns scrim, body lock and the focus trap */
        HV.overlays.open(this);
      } else {
        document.body.classList.add('overlay-open');
      }
      setTimeout(() => $('[data-viewer-close]', this)?.focus({ preventScroll: true }), 60);
    }

    close() {
      const card = this.items && this.items[this.index];
      this.clearStage();
      this.resetZoom();
      this.classList.remove('is-open');
      if (window.HV && HV.overlays) HV.overlays.close();
      else document.body.classList.remove('overlay-open');

      /* LEAVE THE GALLERY WHERE THE SHOPPER LEFT IT. Closing on medium 4 should
         not snap the page back to medium 1 — the viewer reports the index it
         ended on and hv-gallery selects it. The index travels as its own
         attribute rather than as a position in the trigger list, because an
         external video or a 3D model is not a trigger and would shift it. */
      if (card && this.scope) {
        const i = Number(card.dataset.mediaIndex);
        if (!Number.isNaN(i)) {
          this.scope.dispatchEvent(new CustomEvent('hv:viewer-close', {
            bubbles: true, detail: { index: i },
          }));
        }
      }
      /* focus goes back to the medium being looked at, and preventScroll keeps
         the page exactly where it was — no jump to top, no reflow */
      const back = (card && card.hasAttribute('tabindex')) ? card : this.returnTo;
      try { back?.focus({ preventScroll: true }); } catch (e) { /* card may be gone */ }
    }

    step(direction) {
      if (!this.items || this.items.length < 2) return;
      /* a playing video must stop before it is replaced, or its audio outlives it */
      this.clearStage();
      this.index = (this.index + direction + this.items.length) % this.items.length;
      this.render();
    }

    clearStage() {
      $$('video', this.stage || this).forEach((v) => { v.pause(); v.removeAttribute('src'); v.load?.(); });
      if (this.stage) this.stage.innerHTML = '';
      this.media = null;
    }

    /* ── zoom state ────────────────────────────────────────────────────── */

    /* A POINTERUP CAN GO MISSING — released outside the window, swallowed when
       the tab loses focus, or cancelled by the OS. Without one place to end a
       gesture, the drag state survived it: the grab cursor stayed on for good
       and the next press resumed a pan that was over. Every path that can end a
       gesture funnels through here. */
    endDrag() {
      this.drag = null;
      this.pinchFrom = 0;
      if (this.pointers) this.pointers.clear();
      this.classList.remove('is-grabbing', 'is-pinching');
    }

    resetZoom() {
      this.scale = 1; this.tx = 0; this.ty = 0;
      this.baseW = 0; this.baseH = 0; this.maxScale = 1;
      this.endDrag();
      this.classList.remove('is-pannable');
      this.applyTransform();
      this.updateZoomUI();
    }

    /* MAX ZOOM IS THE SOURCE'S DECISION, NOT A CONSTANT. The ceiling is the
       point at which one image pixel is already one device pixel: past it the
       viewer would be inventing detail. A 3000px original in a 750px frame
       gives 4x of real inspection; a 900px original gives about 1.2x, and the
       honest answer there is to stop — and to tell the merchant (CLAUDE.md
       §4.9) rather than to interpolate and call it zoom. */
    measureBase() {
      if (!this.media || this.media.tagName !== 'IMG') { this.updateZoomUI(); return; }
      this.baseW = this.media.offsetWidth || this.media.clientWidth || 0;
      this.baseH = this.media.offsetHeight || this.media.clientHeight || 0;
      const card = this.items && this.items[this.index];
      const nat = Number(card && card.dataset.viewerWidth) || 0;
      const native = Math.min(nat || MAX_SRC, MAX_SRC);
      const ideal = this.baseW ? native / (this.baseW * dpr()) : 3;
      this.maxScale = clamp(ideal, 1, 4);
      this.clampPan();
      this.applyTransform();
      this.updateZoomUI();
    }

    canZoom() {
      return this.maxScale > ZOOM_FLOOR && !!this.media && this.media.tagName === 'IMG';
    }

    applyTransform() {
      if (!this.media) return;
      this.media.style.transform =
        'translate(' + this.tx.toFixed(2) + 'px,' + this.ty.toFixed(2) + 'px) scale(' + this.scale.toFixed(4) + ')';
    }

    /* The photograph may be panned only as far as its own edges — never into
       empty space, which is what makes a zoomed image feel attached to the frame. */
    clampPan() {
      const vw = this.stage.clientWidth, vh = this.stage.clientHeight;
      /* measured live, not from the cached base: swapping in a larger rendition
         mid-zoom can shift the laid-out size by a pixel, and a stale base let
         the pan drift a little past the edge */
      const bw = (this.media && this.media.offsetWidth) || this.baseW;
      const bh = (this.media && this.media.offsetHeight) || this.baseH;
      const sw = bw * this.scale, sh = bh * this.scale;
      const maxX = Math.max(0, (sw - vw) / 2);
      const maxY = Math.max(0, (sh - vh) / 2);
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
    }

    updateZoomUI() {
      const can = this.canZoom();
      if (this.zoomBox) this.zoomBox.hidden = !can;
      if (this.level) this.level.textContent = can ? this.scale.toFixed(1) + '×' : '';
      if (this.zoomIn) this.zoomIn.disabled = !can || this.scale >= this.maxScale - 0.01;
      if (this.zoomOut) this.zoomOut.disabled = !can || this.scale <= 1.01;
      const bw = (this.media && this.media.offsetWidth) || this.baseW;
      const bh = (this.media && this.media.offsetHeight) || this.baseH;
      const pannable = this.scale > 1.01 &&
        (bw * this.scale > this.stage.clientWidth + 1 || bh * this.scale > this.stage.clientHeight + 1);
      this.classList.toggle('is-pannable', pannable);
      /* self-heal: a pointerup lost off-screen or swallowed by a capture change
         would otherwise leave the grabbing cursor stuck on for good. At fit
         scale there is nothing to pan, so grabbing cannot be correct there
         under any circumstances. */
      if (!this.drag || this.scale <= 1.01) this.classList.remove('is-grabbing');
      this.setAttribute('data-zoom', !can ? 'none' : (this.scale > 1.01 ? 'max' : 'in'));
    }

    pointOf(e) {
      const r = this.stage.getBoundingClientRect();
      return { x: e.clientX - (r.left + r.width / 2), y: e.clientY - (r.top + r.height / 2) };
    }

    setZoom(next, px, py) {
      if (!this.media || this.media.tagName !== 'IMG') return;
      next = clamp(next, 1, this.maxScale);
      if (Math.abs(next - this.scale) < 0.0005) return;
      const cx = px === undefined ? 0 : px;
      const cy = py === undefined ? 0 : py;
      /* keep whatever sits under the cursor pinned while the scale changes */
      const k = next / this.scale;
      this.tx = cx - k * (cx - this.tx);
      this.ty = cy - k * (cy - this.ty);
      this.scale = next;
      if (this.scale <= 1.001) { this.scale = 1; this.tx = 0; this.ty = 0; }
      this.clampPan();
      this.applyTransform();
      this.updateZoomUI();
      this.ensureResolution();
    }

    nudgeZoom(dir) {
      if (!this.canZoom()) return;
      const steps = [1, 1.5, 2, 2.5, 3, 4].filter((s) => s <= this.maxScale + 0.001);
      if (steps[steps.length - 1] < this.maxScale) steps.push(this.maxScale);
      let i = 0;
      for (let n = 0; n < steps.length; n++) if (steps[n] <= this.scale + 0.01) i = n;
      this.setZoom(steps[clamp(i + dir, 0, steps.length - 1)]);
    }

    toggleZoomAt(e) {
      if (!this.canZoom()) return;
      if (this.scale > 1.01) { this.setZoom(1); return; }
      const p = this.pointOf(e);
      this.setZoom(Math.min(2, this.maxScale), p.x, p.y);
    }

    /* AS THE SCALE CLIMBS, ASK FOR MORE PIXELS. Without this, zooming is just
       CSS enlarging the fit-sized file and the jewellery turns to mush at 2x —
       which is the specific failure this viewer exists to avoid. The new file
       is decoded off-screen first, so the swap never flashes. */
    ensureResolution() {
      const card = this.items && this.items[this.index];
      if (!card || !this.media || this.media.tagName !== 'IMG') return;
      const template = card.dataset.viewerImage;
      if (!template) return;
      const need = Math.ceil(this.baseW * this.scale * dpr());
      if (need <= (this.requested || 0)) return;
      const url = sizedUrl(template, card.dataset.viewerWidth, need);
      this.requested = need;
      if (url === this.currentUrl) return;
      const pre = new Image();
      pre.decoding = 'async';
      pre.onload = () => {
        if (this.media && this.media.tagName === 'IMG' && this.items[this.index] === card) {
          this.media.src = url;
          this.currentUrl = url;
        }
      };
      pre.src = url;
    }

    /* ── gestures ──────────────────────────────────────────────────────── */

    onPointerDown(e) {
      /* BEFORE ANY EARLY RETURN. A fresh press with nothing else down means the
         previous gesture is over, whether or not its pointerup ever arrived —
         and the presses most likely to follow a lost one are on the zoom
         buttons, which return below. Cleaning up after that return is how the
         grab cursor stayed stuck through six clicks of zoom-out. */
      if (this.pointers.size === 0) this.endDrag();
      if (e.target.closest('button, a[href]')) return;
      if (this.media && this.media.tagName === 'VIDEO') return;   /* its controls win */
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2) {
        this.pinchFrom = this.pinchSpan();
        this.pinchScale = this.scale;
        this.drag = null;
        this.classList.add('is-pinching');
        return;
      }
      /* POINTER CAPTURE IS TAKEN AT THE THRESHOLD, NOT HERE — the same rule the
         rail drag learned in §4.3, and for a sharper reason. Capturing on
         pointerdown retargets the subsequent pointerup AND the click to the
         capturing element, so a plain click on the photograph arrived as a
         click on .viewer__stage — which is the backdrop, which closes. Click to
         zoom could never work while this ran eagerly. A press that stays a
         press now keeps entirely native targeting. */
      this.drag = {
        x: e.clientX, y: e.clientY, tx: this.tx, ty: this.ty,
        t: Date.now(), moved: false, captured: false,
        /* remember what was really pressed: once capture IS taken, every later
           event reports the stage, so the original target is the only honest
           record of whether the photograph itself was pressed */
        target: e.target,
      };
      if (this.scale > 1.01) this.classList.add('is-grabbing');
    }

    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2) {
        const span = this.pinchSpan();
        if (this.pinchFrom > 0) {
          const mid = this.pinchMid();
          this.setZoom(this.pinchScale * (span / this.pinchFrom), mid.x, mid.y);
        }
        return;
      }
      if (!this.drag) return;
      const dx = e.clientX - this.drag.x, dy = e.clientY - this.drag.y;
      if (!this.drag.moved && Math.hypot(dx, dy) > 6) this.drag.moved = true;
      /* now that it is genuinely a drag, capture — so the pan survives the
         cursor leaving the photograph, without costing a plain click its target */
      if (this.drag.moved && !this.drag.captured && this.scale > 1.01) {
        this.drag.captured = true;
        try { this.stage.setPointerCapture(e.pointerId); } catch (err) { /* fine */ }
      }
      /* PAN ONLY WHEN THERE IS SOMEWHERE TO PAN. At fit scale a horizontal drag
         belongs to the gallery, not to the image — which is how swiping between
         photographs and panning inside one stop fighting each other. */
      if (this.scale > 1.01) {
        this.tx = this.drag.tx + dx;
        this.ty = this.drag.ty + dy;
        this.clampPan();
        this.applyTransform();
      }
    }

    onPointerUp(e) {
      const last = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) { this.classList.remove('is-pinching'); this.pinchFrom = 0; }
      this.classList.remove('is-grabbing');
      try { this.stage.releasePointerCapture(e.pointerId); } catch (err) { /* fine */ }
      if (!this.drag) return;

      const d = this.drag; this.drag = null;
      const ex = last ? last.x : e.clientX, ey = last ? last.y : e.clientY;
      const dx = ex - d.x, dy = ey - d.y;
      const dist = Math.hypot(dx, dy);

      if (d.moved) {
        /* a drag must never also register as a click on the backdrop */
        this.swallowClick = true;
        /* at fit scale, a horizontal flick moves through the product's media */
        if (this.scale <= 1.01 && Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4 && (Date.now() - d.t) < 900) {
          this.step(dx < 0 ? 1 : -1);
        }
        return;
      }
      if (dist > 6) return;

      if (e.pointerType === 'mouse') {
        if (d.target === this.media) { this.swallowClick = true; this.toggleZoomAt(e); }
        return;
      }
      /* touch: a second tap inside 300ms is a double-tap */
      const now = Date.now();
      if (now - (this.lastTap || 0) < 300 && d.target === this.media) {
        this.lastTap = 0;
        this.swallowClick = true;
        this.toggleZoomAt(e);
      } else {
        this.lastTap = now;
      }
    }

    pinchSpan() {
      const p = Array.from(this.pointers.values());
      if (p.length < 2) return 0;
      return Math.hypot(p[0].x - p[1].x, p[0].y - p[1].y);
    }

    pinchMid() {
      const p = Array.from(this.pointers.values());
      const r = this.stage.getBoundingClientRect();
      const mx = (p[0].x + p[1].x) / 2, my = (p[0].y + p[1].y) / 2;
      return { x: mx - (r.left + r.width / 2), y: my - (r.top + r.height / 2) };
    }

    onWheel(e) {
      if (!this.canZoom()) return;
      e.preventDefault();
      const p = this.pointOf(e);
      /* exponential so a trackpad and a notched wheel both feel proportional */
      this.setZoom(this.scale * Math.exp(-e.deltaY * 0.0016), p.x, p.y);
    }

    /* ── render ────────────────────────────────────────────────────────── */

    render() {
      const card = this.items[this.index];
      if (!card || !this.stage) return;
      const d = card.dataset;
      this.clearStage();
      this.resetZoom();
      this.requested = 0;
      this.currentUrl = '';

      if (d.viewerVideo) {
        const video = document.createElement('video');
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        /* Sound is never automatic. The viewer is a deliberate act, so the
           controls are there — but it opens muted and the visitor unmutes.

           BOTH the property and the attribute, the same belt-and-braces
           havani-feed.js uses: the property governs playback, but Safari reads
           the ATTRIBUTE when deciding whether muted autoplay is permitted at
           all. With only the property set, the play() below can be refused and
           the visitor gets a still frame with no obvious reason why. */
        video.muted = true;
        video.setAttribute('muted', '');
        if (d.viewerPoster) video.poster = d.viewerPoster;
        video.src = d.viewerVideo;
        video.className = 'viewer__media';
        video.dataset.kind = 'video';
        this.media = video;
        this.stage.appendChild(video);
        video.play().catch(() => {});
      } else if (d.viewerImage) {
        const img = document.createElement('img');
        img.className = 'viewer__media';
        img.alt = d.viewerAlt || '';
        img.decoding = 'async';
        /* the high-resolution fetch happens HERE, on open, and again on zoom */
        const target = Math.round(Math.min(window.innerWidth, 1600) * dpr());
        const url = sizedUrl(d.viewerImage, d.viewerWidth, target);
        this.requested = target;
        this.currentUrl = url;
        img.addEventListener('load', () => this.measureBase(), { once: true });
        img.src = url;
        this.media = img;
        this.stage.appendChild(img);
        if (img.complete) this.measureBase();
      }

      if (this.caption) {
        this.caption.textContent = d.viewerCaption || '';
        this.caption.hidden = !d.viewerCaption;
      }
      if (this.counter) {
        const many = this.items.length > 1;
        this.counter.textContent = many ? (this.index + 1) + ' / ' + this.items.length : '';
        this.counter.hidden = !many;
      }
      const nav = this.items.length > 1;
      $$('[data-viewer-prev],[data-viewer-next]', this).forEach((b) => { b.hidden = !nav; });
      this.updateZoomUI();
    }
  }
  if (!customElements.get('hv-viewer')) customElements.define('hv-viewer', HvViewer);
})();
